import { X, RotateCcw } from 'lucide-react';
import type { Job } from '@/lib/types';
import JobStatusBadge from './JobStatusBadge';
import FormattedDate from '@/components/common/FormattedDate';
import { formatDuration } from '@/lib/utils';

// Statuses that can be cancelled (running/failed jobs cancellation not yet supported)
const CANCELLABLE_STATUSES = ['pending', 'retry_pending'];
// Statuses that can be retried
const RETRYABLE_STATUSES = ['failed', 'cancelled'];

interface JobsTableProps {
  jobs: Job[];
  onJobClick?: (job: Job) => void;
  onCancelJob?: (jobId: string) => void;
  onRetryJob?: (jobId: string) => void;
  cancellingJobId?: string | null;
  retryingJobId?: string | null;
  loading?: boolean;
}

export default function JobsTable({ jobs, onJobClick, onCancelJob, onRetryJob, cancellingJobId, retryingJobId, loading }: JobsTableProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading jobs...</div>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-medium">No jobs found</p>
          <p className="text-sm mt-1">Jobs will appear here when they are created</p>
        </div>
      </div>
    );
  }

  const getActiveWorker = (job: Job): string | null => {
    if (!job.assignments || job.assignments.length === 0) {
      return null;
    }

    // Find active assignment (no release_ts) or most recent one
    const activeAssignment = job.assignments.find(a => !a.release_ts);
    if (activeAssignment) {
      return activeAssignment.worker_id;
    }

    // If all released, return the most recent one (last in array)
    return job.assignments[job.assignments.length - 1].worker_id;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Job ID
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Worker ID
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Status
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Type
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Created
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Duration
            </th>
            {(onCancelJob || onRetryJob) && (
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr
              key={job.id}
              onClick={() => onJobClick?.(job)}
              className="border-b border-border hover:bg-muted/30 cursor-pointer transition-colors"
            >
              <td className="py-3 px-4">
                <code className="text-xs text-primary font-mono whitespace-nowrap">
                  {job.id}
                </code>
              </td>
              <td className="py-3 px-4">
                {getActiveWorker(job) ? (
                  <code className="text-xs text-muted-foreground font-mono">
                    {getActiveWorker(job)}
                  </code>
                ) : (
                  <span className="text-xs text-muted-foreground">-</span>
                )}
              </td>
              <td className="py-3 px-4">
                <JobStatusBadge status={job.status_name} />
              </td>
              <td className="py-3 px-4">
                <span className="text-sm">{job.type_name}</span>
              </td>
              <td className="py-3 px-4">
                <span className="text-sm text-muted-foreground">
                  <FormattedDate date={job.create_ts} />
                </span>
              </td>
              <td className="py-3 px-4">
                <span className="text-sm text-muted-foreground">
                  {formatDuration(job.start_ts, job.complete_ts)}
                </span>
              </td>
              {(onCancelJob || onRetryJob) && (
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    {onCancelJob && CANCELLABLE_STATUSES.includes(job.status_name) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Cancel job ${job.id}?`)) {
                            onCancelJob(job.id);
                          }
                        }}
                        disabled={cancellingJobId === job.id}
                        className="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Cancel job"
                      >
                        <X className="h-3 w-3" />
                        {cancellingJobId === job.id ? 'Cancelling...' : 'Cancel'}
                      </button>
                    )}
                    {onRetryJob && RETRYABLE_STATUSES.includes(job.status_name) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Retry job ${job.id}?`)) {
                            onRetryJob(job.id);
                          }
                        }}
                        disabled={retryingJobId === job.id}
                        className="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Retry job"
                      >
                        <RotateCcw className="h-3 w-3" />
                        {retryingJobId === job.id ? 'Retrying...' : 'Retry'}
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
